/**
 * Phase 4g.C (Feb 2026) — Werkstatt-Aufträge geteilte Konstanten + Formatter.
 *
 * Aus `WorkshopOrdersPage.jsx` extrahiert, damit die jetzt aufgesplitteten
 * Komponenten (OrderCard, WorkOrderModal) ohne Default-Page-Import die
 * gleichen Konstanten teilen können. Reine Datenkonstanten — keine Side-Effekte.
 */

export const fmtEur = (v) =>
    Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export const fmtNum = (v) =>
    Number(v || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

export const STATUS_META = {
    draft: { label: "Entwurf", color: "bg-gray-100 text-gray-700", next: ["quote", "confirmed"] },
    quote: { label: "Angebot", color: "bg-purple-100 text-purple-800", next: ["confirmed"] },
    confirmed: { label: "Auftrag", color: "bg-blue-100 text-blue-800", next: ["in_progress"] },
    in_progress: { label: "In Arbeit", color: "bg-amber-100 text-amber-900", next: ["completed"] },
    completed: { label: "Fertig", color: "bg-green-100 text-green-800", next: ["invoiced"] },
    invoiced: { label: "Abgerechnet", color: "bg-emerald-200 text-emerald-900", next: [] },
    cancelled: { label: "Storniert", color: "bg-red-100 text-red-700", next: [] },
};

export const STATUS_NEXT_LABEL = {
    quote: "→ Angebot",
    confirmed: "→ Auftrag",
    in_progress: "→ Start",
    completed: "→ Fertig (ohne Rechnung)",
    invoiced: "→ Abrechnen",
};

// Auftrags-Farbcodierung (Jun 2026, User-Wunsch):
//   intern = Gelb · Kunde = Grau · GW = Blau · Garantie = Rot.
// Bei Misch-Aufträgen werden mehrere Farben hälftig/segmentiert gezeigt.
export const ORDER_CATEGORY_META = {
    customer: { key: "customer", label: "Kunde (extern)", hex: "#9CA3AF", swatch: "bg-gray-400" },
    warranty: { key: "warranty", label: "Garantie (intern /extern)", hex: "#EF4444", swatch: "bg-red-500" },
    gw: { key: "gw", label: "Gebrauchte (intern)", hex: "#3B82F6", swatch: "bg-blue-500" },
    internal: { key: "internal", label: "Auslieferungen (intern)", hex: "#FACC15", swatch: "bg-yellow-400" },
};

/** Jul-2026 (User-Wunsch) — Kennzeichen-Anzeige: ohne Kennzeichen (meist 50-ccm)
 *  werden die letzten 6 Zeichen der Fahrgestellnummer als Kennung genutzt. */
export function plateLabel(v) {
    if (!v) return "";
    if (v.license_plate) return v.license_plate;
    const vin = (v.vin || "").trim();
    return vin.length >= 6 ? vin.slice(-6) : vin;
}

export const ORDER_CATEGORY_LIST = ["customer", "warranty", "gw", "internal"];

/**
 * Liefert die Abrechnungs-Kategorien eines Auftrags als Meta-Objekte
 * (für die Farbcodierung). Bevorzugt die serverseitig berechneten
 * `order.categories`-Keys; fällt sonst auf die Ableitung aus den Positionen
 * zurück (intern → GW, falls eigenes Fahrzeug verknüpft).
 */
export function orderCategories(order) {
    let keys = Array.isArray(order?.categories) ? order.categories : null;
    if (!keys) {
        const hasOwnVehicle = !!(order?.internal_vehicle_listing_id || order?.vehicle?.listing_id);
        const deliveryPrep = !!order?.delivery_prep;
        const found = new Set();
        for (const p of order?.positions || []) {
            const bc = p.billing_category || "customer";
            if (bc === "internal") found.add(deliveryPrep ? "internal" : (hasOwnVehicle ? "gw" : "internal"));
            else if (bc === "warranty") found.add("warranty");
            else found.add("customer");
        }
        if (found.size === 0) found.add("customer");
        keys = ORDER_CATEGORY_LIST.filter((k) => found.has(k));
    }
    return keys.map((k) => ORDER_CATEGORY_META[k]).filter(Boolean);
}
